import { FC, type ReactNode } from 'react';

import ChevronLeftIcon from '@/assets/svg/ui-elements/chevron-left.svg';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { Alert, Button, Tabs, theme, Typography } from '@/shared/design-system';

import { BackRow, Board, BoardToolbar, PageHeader } from '../styled';

export const BOARD_VIEWS = {
  list: 'list',
  compare: 'compare',
} as const;

export type BoardView = (typeof BOARD_VIEWS)[keyof typeof BOARD_VIEWS];

export const toBoardView = (value: string | null): BoardView =>
  value === BOARD_VIEWS.compare ? BOARD_VIEWS.compare : BOARD_VIEWS.list;

export const READ_ONLY_TITLE = 'You can view roles but not change them';

export type BoardHeaderProps = {
  title: string;
  description: string;
  view: BoardView;
  onViewChange: (_view: BoardView) => void;
};

/** Title, the one rule worth stating up front, and the List / Compare switch. */
export const BoardHeader: FC<BoardHeaderProps> = ({ title, description, view, onViewChange }) => (
  <BoardToolbar>
    <PageHeader>
      <Typography variant="h5" color={theme.colors.text.primary}>
        {title}
      </Typography>
      <Typography variant="body3" color={theme.colors.text.secondary}>
        {description}
      </Typography>
    </PageHeader>

    <Tabs.Root
      variant="boxed"
      size="sm"
      value={view}
      onValueChange={(value) => onViewChange(toBoardView(String(value)))}
    >
      <Tabs.List>
        <Tabs.Tab value={BOARD_VIEWS.list} label="List" />
        <Tabs.Tab value={BOARD_VIEWS.compare} label="Compare" />
      </Tabs.List>
    </Tabs.Root>
  </BoardToolbar>
);

/**
 * Said once, up front, instead of leaving a viewer to wonder why every control is missing or
 * disabled.
 */
export const ReadOnlyNotice: FC<{ description: string }> = ({ description }) => (
  <Alert variant="info" title={READ_ONLY_TITLE} description={description} />
);

export const ALL_ROLES_LABEL = 'All roles';

export type BoardPanesProps = {
  list: ReactNode;
  detail: ReactNode;
  /** On a phone the board shows one pane at a time; this says the role is open. */
  isDetailOpen: boolean;
  onBack: () => void;
};

/**
 * Side by side on a wide screen. On a phone, list then detail as two steps, so an opened role
 * is never pushed below a screen-long list of every other role.
 */
export const BoardPanes: FC<BoardPanesProps> = ({ list, detail, isDetailOpen, onBack }) => {
  const isMobile = useIsMobile();

  if (!isMobile) {
    return (
      <Board>
        {list}
        {detail}
      </Board>
    );
  }

  if (!isDetailOpen) return <Board>{list}</Board>;

  return (
    <Board>
      <BackRow>
        <Button size="sm" variant="ghost" color="tertiary" onClick={onBack}>
          <ChevronLeftIcon aria-hidden />
          {ALL_ROLES_LABEL}
        </Button>
      </BackRow>
      {detail}
    </Board>
  );
};
